import type { Metadata } from "next";
import { Inter, IBM_Plex_Mono } from "next/font/google";
import "./globals.css";
import { SiteHeader } from "@/components/layout/site-header";
import { SiteFooter } from "@/components/layout/site-footer";
import { AuthSessionProvider } from "@/components/providers/session-provider";
import { CartSync } from "@/components/cart/cart-sync";
import { Toaster } from "sonner";
import Script from "next/script";
const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || "https://jourjcreation.com";

const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Jour J Création",
  url: siteUrl,
  logo: `${siteUrl}/icon.png`,
  image: `${siteUrl}/og-image.jpg`,
  description:
    "Jour J Création propose des laines teintes à la main, des pelotes uniques et des kits créatifs dans un univers artisanal, sensible et soigneusement composé.",
};

const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Jour J Création",
  url: siteUrl,
  description:
    "Boutique de laines teintes à la main, pelotes uniques et kits créatifs.",
  inLanguage: "fr-FR",
};

const inter = Inter({
  subsets: ["latin"],
  variable: "--font-body",
  display: "swap",
});

const ibmPlexMono = IBM_Plex_Mono({
  subsets: ["latin"],
  weight: ["400", "500"],
  variable: "--font-ibm-plex-mono",
  display: "swap",
});

export const metadata: Metadata = {
  metadataBase: new URL(siteUrl),
  title: {
    default: "Jour J Création | Laines teintes à la main",
    template: "%s | Jour J Création",
  },
  description:
    "Jour J Création propose des laines teintes à la main, des pelotes uniques et des kits créatifs dans un univers artisanal, sensible et soigneusement composé.",
  applicationName: "Jour J Création",
  authors: [{ name: "Jour J Création" }],
  creator: "Jour J Création",
  publisher: "Jour J Création",
  category: "artisanat",
  classification: "Boutique e-commerce de laines teintes à la main",
  referrer: "origin-when-cross-origin",
  keywords: [
    "Jour J Création",
    "laine teinte à la main",
    "laines teintes artisanalement",
    "pelote unique",
    "kit 2 pelotes",
    "kit 3 pelotes",
    "laine artisanale",
    "fil à tricoter teint à la main",
    "teinturière laine",
    "créations laine artisanales",
    "tricot",
    "crochet",
    "écheveaux teints à la main",
  ],
  alternates: {
    canonical: "/",
  },
  formatDetection: {
    telephone: false,
    address: false,
    email: false,
  },
  robots: {
    index: true,
    follow: true,
    googleBot: {
      index: true,
      follow: true,
      "max-video-preview": -1,
      "max-image-preview": "large",
      "max-snippet": -1,
    },
  },
  openGraph: {
    type: "website",
    locale: "fr_FR",
    countryName: "France",
    url: "/",
    siteName: "Jour J Création",
    title: "Jour J Création | Laines teintes à la main",
    description:
      "Découvrez des laines teintes à la main, des pelotes uniques et des kits créatifs dans un univers artisanal, chaleureux et singulier.",
    images: [
      {
        url: "/og-image.jpg",
        width: 1200,
        height: 630,
        alt: "Jour J Création - Laines teintes à la main",
      },
    ],
  },
  twitter: {
    card: "summary_large_image",
    title: "Jour J Création | Laines teintes à la main",
    description:
      "Pelotes uniques, kits créatifs et laines teintes à la main dans un univers artisanal sensible et soigné.",
    images: ["/og-image.jpg"],
    creator: "@jourjcreation",
    site: "@jourjcreation",
  },
  icons: {
    icon: [
      { url: "/favicon.ico" },
      { url: "/icon.png", type: "image/png" },
    ],
    shortcut: ["/favicon.ico"],
    apple: [{ url: "/apple-touch-icon.png" }],
  },
};

export default function RootLayout({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  return (
    <html lang="fr">
      <body className={`${inter.variable} ${ibmPlexMono.variable}`}>
        <Script
          id="jourjcreation-organization-jsonld"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(organizationJsonLd),
          }}
        />
        <Script
          id="jourjcreation-website-jsonld"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(websiteJsonLd),
          }}
        />
        <AuthSessionProvider>
          {/* <CartSync /> */}
          <SiteHeader />
          <main>{children}</main>
          <SiteFooter />
        </AuthSessionProvider>
        <Toaster richColors position="top-right" />
      </body>
    </html>
  );
}